import type { Settlement } from '../map/settlement';
import { getGameConfig } from '../../config/gameConfig';

export type FoodHoursMode = 'global' | 'avg' | 'median' | 'avg+median';

function median(values: number[]): number {
    if (values.length === 0) return Number.POSITIVE_INFINITY;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

function mean(values: number[]): number {
    if (values.length === 0) return Number.POSITIVE_INFINITY;
    let sum = 0;
    for (const value of values) sum += value;
    return sum / values.length;
}

/**
 * Rough estimate: how many hours of food remain in the settlement.
 *
 * Assumption for roughness:
 * - Food is consumed to offset hunger growth on average:
 *   food_per_interval ≈ hungerStep / foodStep
 * - intervals_per_hour = (3600 / tickSeconds) / hungerInterval
 * - So food_per_hour_per_person ≈ (hungerStep / foodStep) * intervals_per_hour
 *
 * Then:
 * - global hours = totalFood / (totalPeople * food_per_hour_per_person)
 * - per-house hours = houseFood / (housePeople * food_per_hour_per_person)
 *   and aggregate with avg/median/(avg+median)/2
 */
export function computeFoodHoursLeft(
    settlement: Settlement,
    mode: FoodHoursMode = 'avg+median',
): number {
    const config = getGameConfig();
    const tickSeconds = config.time.tickSeconds;
    const hunger = config.population.hunger;

    if (tickSeconds <= 0) return Number.POSITIVE_INFINITY;
    if (hunger.hungerInterval <= 0) return Number.POSITIVE_INFINITY;
    if (hunger.hungerStep <= 0) return Number.POSITIVE_INFINITY;
    if (hunger.foodStep <= 0) return 0;

    const ticksPerHour = 3600 / tickSeconds;
    const intervalsPerHour = ticksPerHour / hunger.hungerInterval;
    const foodPerHourPerPerson = (hunger.hungerStep / hunger.foodStep) * intervalsPerHour;
    if (foodPerHourPerPerson <= 0) return Number.POSITIVE_INFINITY;

    let totalFood = 0;
    let totalPeople = 0;
    const perHouseHours: number[] = [];

    for (const house of settlement.houses) {
        const people = house.people?.length ?? 0;
        if (people <= 0) continue;
        const food = house.storage.food ?? 0;

        totalPeople += people;
        totalFood += food;

        const burnHouse = people * foodPerHourPerPerson;
        perHouseHours.push(burnHouse > 0 ? food / burnHouse : Number.POSITIVE_INFINITY);
    }

    if (totalPeople === 0) return Number.POSITIVE_INFINITY;
    if (totalFood <= 0) return 0;

    const globalHours = totalFood / (totalPeople * foodPerHourPerPerson);

    if (mode === 'global') return globalHours;

    const avgHours = mean(perHouseHours);
    const medHours = median(perHouseHours);

    if (mode === 'avg') return avgHours;
    if (mode === 'median') return medHours;

    return (avgHours + medHours) / 2;
}
